import type { InboxViewKey } from '@natashu/shared';
import { useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api.ts';
import { useAuth } from '../../lib/auth.tsx';
import { useRealtimeEvent } from '../../lib/realtime.ts';
import type { ConversationListPage } from './types.ts';

const COUNT_LIMIT = 100;
export const READ_CONVERSATIONS = ['conversations.read_all', 'conversations.read_assigned'] as const;

export const countKey = (view: InboxViewKey | 'starred') => ['inbox-counts', view] as const;

export interface ViewCount {
  count: number;
  /** More than COUNT_LIMIT conversations match (show "99+"). */
  capped: boolean;
}

/**
 * Cheap view count: one list page of ids (the API has no total). Uses its own key so realtime bursts
 * refresh it on a debounce instead of on every event.
 */
export function useInboxViewCount(view: InboxViewKey, enabled: boolean) {
  const queryClient = useQueryClient();
  useRealtimeEvent('*', (event) => {
    if (!enabled) return;
    if (event.type === 'message.created' || event.type === 'conversation.updated') scheduleCountRefresh(queryClient);
  });
  useRealtimeEvent('resync', () => scheduleCountRefresh(queryClient));
  return useQuery({
    queryKey: countKey(view),
    queryFn: async ({ signal }): Promise<ViewCount> => {
      const page = await api.get<ConversationListPage>('/api/conversations', { query: { view, limit: COUNT_LIMIT }, signal });
      return { count: page.items.length + (page.pinned?.length ?? 0), capped: page.nextCursor !== null };
    },
    enabled,
    staleTime: 20_000,
    refetchInterval: 120_000,
  });
}

let refreshTimer: number | undefined;
function scheduleCountRefresh(queryClient: QueryClient): void {
  if (refreshTimer !== undefined) return;
  refreshTimer = window.setTimeout(() => {
    refreshTimer = undefined;
    void queryClient.invalidateQueries({ queryKey: ['inbox-counts'] });
  }, 1_500);
}

/** Needs-reply count for the topbar bell (null when the user cannot read conversations). */
export function useNeedsReplyCount(): number | null {
  const { can } = useAuth();
  const allowed = can(READ_CONVERSATIONS);
  const { data } = useInboxViewCount('needs_reply', allowed);
  if (!allowed || !data) return null;
  return data.capped ? Math.max(data.count, COUNT_LIMIT) : data.count;
}

export function formatCount(c: ViewCount | undefined): string | undefined {
  if (!c) return undefined;
  return c.capped ? `${COUNT_LIMIT - 1}+` : String(c.count);
}

/**
 * How many chats are starred right now — the star on the button row blinks while there are any, and is a plain
 * star when there are none (owner, 30 ก.ย. 2569: "ติดดาวก็กระพริบด้วยครับ ถ้ามีรายการติดดาวอยู่").
 *
 * Starring is a filter rather than a view, so it cannot go through `useInboxViewCount`; one page of ids is
 * enough, since the answer needed is only "any or none".
 */
export function useStarredCount(enabled: boolean) {
  const queryClient = useQueryClient();
  useRealtimeEvent('*', (event) => {
    if (!enabled) return;
    if (event.type === 'message.created' || event.type === 'conversation.updated') scheduleCountRefresh(queryClient);
  });
  return useQuery({
    queryKey: countKey('starred'),
    queryFn: async ({ signal }): Promise<ViewCount> => {
      const page = await api.get<ConversationListPage>('/api/conversations', { query: { view: 'all', starred: '1', limit: COUNT_LIMIT }, signal });
      return { count: page.items.length + (page.pinned?.length ?? 0), capped: page.nextCursor !== null };
    },
    enabled,
    staleTime: 20_000,
    refetchInterval: 120_000,
  });
}
